import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, Plus } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { Files } from "./Files";
import {
  nok, today, JOB_STATUS, CATEGORIES, PAYMENT, label, Field, Input, Area, Select, Btn, Card, Badge, PageHead, Sheet, useData, ExportBtn, Spinner, RowsEditor,
} from "./ui";

const TONE = { ny: "red", pagar: "sky", venter_deler: "amber", fullfort: "green", levert: "zinc" };
const PAY_TONE = { ubetalt: "red", delvis: "amber", betalt: "green" };
const EMPTY = { customer_id: "", vehicle_id: "", category: "diagnose", status: "ny", date: today(), fault_codes: "", findings: "", work_done: "", ecu_info: "",
  notes: "", parts: [], labour_hours: 0, labour_rate: 0, vat_rate: 25, amount_paid: 0 };

const calc = (o) => {
  const parts = o.parts.reduce((s, p) => s + (Number(p.qty) || 0) * (Number(p.unit_price) || 0), 0);
  const total = parts + (Number(o.labour_hours) || 0) * (Number(o.labour_rate) || 0);
  const vat = (total * Number(o.vat_rate)) / (100 + Number(o.vat_rate));
  return { parts, total, vat, outstanding: Math.max(total - (Number(o.amount_paid) || 0), 0) };
};

export const WorkOrderForm = ({ initial, onClose, onSaved }) => {
  const [o, setO] = useState({ ...EMPTY, ...initial });
  const [customers, setCustomers] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/customers").then((r) => setCustomers(Array.isArray(r.data) ? r.data : [])).catch(() => {}); }, []);
  const cust = customers.find((c) => c.id === o.customer_id);
  const set = (k) => (e) => setO({ ...o, [k]: e.target.value });
  const c = calc(o);
  const save = async () => {
    if (!o.customer_id) return toast.error("Velg kunde");
    setBusy(true);
    const body = { ...o, vehicle_id: o.vehicle_id || null, labour_hours: Number(o.labour_hours) || 0, labour_rate: Number(o.labour_rate) || 0,
      amount_paid: Number(o.amount_paid) || 0, vat_rate: Number(o.vat_rate), parts: o.parts.map((p) => ({ name: p.name, qty: Number(p.qty) || 0, unit_price: Number(p.unit_price) || 0 })) };
    try {
      const { data } = o.id ? await api.put(`/admin/work-orders/${o.id}`, body) : await api.post("/admin/work-orders", body);
      setO({ ...EMPTY, ...data, parts: data.parts.map((p, i) => ({ id: `p${i}`, ...p })) });
      toast.success("Arbeidsordre lagret");
      onSaved?.();
    } catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };
  return (
    <Sheet title={o.number ? `Arbeidsordre ${o.number}` : "Ny arbeidsordre"} onClose={onClose} testid="order-sheet">
      <div className="space-y-6">
        <Card className="grid gap-3 md:grid-cols-4">
          <Field label="Kunde *" className="md:col-span-2">
            <Select value={o.customer_id} onChange={(e) => setO({ ...o, customer_id: e.target.value, vehicle_id: "" })} data-testid="order-customer-select"
              options={[["", "Velg kunde…"], ...customers.map((x) => [x.id, `${x.name}${x.phone ? ` · ${x.phone}` : ""}`])]} />
          </Field>
          <Field label="Kjøretøy" className="md:col-span-2">
            <Select value={o.vehicle_id ?? ""} onChange={set("vehicle_id")} data-testid="order-vehicle-select"
              options={[["", "Ikke valgt"], ...(cust?.vehicles ?? []).map((v) => [v.id, `${v.reg_nr} ${v.make} ${v.model}`.trim()])]} />
          </Field>
          <Field label="Kategori"><Select value={o.category} onChange={set("category")} options={CATEGORIES} data-testid="order-category-select" /></Field>
          <Field label="Status"><Select value={o.status} onChange={set("status")} options={JOB_STATUS} data-testid="order-status-select" /></Field>
          <Field label="Dato"><Input type="date" value={o.date ?? ""} onChange={set("date")} /></Field>
          <Field label="MVA-sats"><Select value={String(o.vat_rate)} onChange={(e) => setO({ ...o, vat_rate: Number(e.target.value) })} options={[["25", "25 %"], ["0", "0 %"]]} /></Field>
        </Card>
        <Card className="grid gap-3 md:grid-cols-2">
          <Field label="Feilkoder (DTC)"><Area value={o.fault_codes} onChange={set("fault_codes")} placeholder="P0299, P2263 …" data-testid="order-dtc-input" /></Field>
          <Field label="Diagnose / funn"><Area value={o.findings} onChange={set("findings")} data-testid="order-findings-input" /></Field>
          <Field label="Utført arbeid"><Area value={o.work_done} onChange={set("work_done")} data-testid="order-work-input" /></Field>
          <Field label="ECU/TCU-info (styreenhet, programvare, fil)"><Area value={o.ecu_info} onChange={set("ecu_info")} data-testid="order-ecu-input" /></Field>
          <Field label="Interne notater" className="md:col-span-2"><Area value={o.notes} onChange={set("notes")} /></Field>
        </Card>
        <Card>
          <h3 className="mb-3 font-display text-xl font-bold uppercase">Deler</h3>
          <RowsEditor rows={o.parts} setRows={(parts) => setO({ ...o, parts })} addLabel="Legg til del" testid="order-parts" blank={() => ({ name: "", qty: 1, unit_price: "" })}
            render={(p, patch, i) => (<>
              <Field label="Del" className="md:col-span-6"><Input value={p.name} onChange={(x) => patch({ name: x.target.value })} data-testid={`order-part-name-${i}`} /></Field>
              <Field label="Antall" className="md:col-span-2"><Input type="number" min="0" value={p.qty} onChange={(x) => patch({ qty: x.target.value })} /></Field>
              <Field label="Pris/stk inkl. MVA" className="md:col-span-3"><Input type="number" min="0" value={p.unit_price} onChange={(x) => patch({ unit_price: x.target.value })} data-testid={`order-part-price-${i}`} /></Field>
            </>)} />
        </Card>
        <Card className="grid gap-3 md:grid-cols-4">
          <Field label="Arbeidstimer"><Input type="number" min="0" step="0.25" value={o.labour_hours} onChange={set("labour_hours")} data-testid="order-hours-input" /></Field>
          <Field label="Timepris inkl. MVA"><Input type="number" min="0" value={o.labour_rate} onChange={set("labour_rate")} data-testid="order-rate-input" /></Field>
          <Field label="Innbetalt"><Input type="number" min="0" value={o.amount_paid} onChange={set("amount_paid")} data-testid="order-paid-input" /></Field>
          <div className="border border-white/10 p-3" data-testid="order-totals">
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">Totalt inkl. MVA</div>
            <div className="font-display text-2xl font-bold" data-testid="order-total">{nok(c.total)}</div>
            <div className="text-xs text-zinc-400">herav MVA {nok(c.vat)} · utestående {nok(c.outstanding)}</div>
          </div>
        </Card>
        <Card><h3 className="mb-3 font-display text-xl font-bold uppercase">Vedlegg</h3><Files ownerType="work_order" ownerId={o.id} testid="order-files" /></Card>
        <div className="sticky bottom-0 flex gap-2 border-t border-white/10 bg-[#08090b] py-4">
          <Btn variant="red" onClick={save} disabled={busy} data-testid="order-save">Lagre</Btn>
          <Btn onClick={onClose}>Lukk</Btn>
        </div>
      </div>
    </Sheet>
  );
};

export const OrderRow = ({ o, onClick }) => (
  <button type="button" onClick={onClick} data-testid={`order-row-${o.id}`} className="grid w-full gap-2 border border-white/10 bg-[var(--surface)] p-4 text-left hover:border-white/30 md:grid-cols-12 md:items-center">
    <div className="md:col-span-4">
      <div className="font-mono text-xs text-zinc-400">{o.number} · {o.date}</div>
      <div className="font-display text-xl font-bold uppercase">{o.customer_name || "–"}</div>
      <div className="text-xs text-zinc-400">{o.vehicle ? `${o.vehicle.reg_nr} ${o.vehicle.make} ${o.vehicle.model}` : ""}</div>
    </div>
    <div className="flex flex-wrap gap-2 md:col-span-5"><Badge>{label(CATEGORIES, o.category)}</Badge><Badge tone={TONE[o.status]}>{label(JOB_STATUS, o.status)}</Badge><Badge tone={PAY_TONE[o.calc.payment_status]}>{label(PAYMENT, o.calc.payment_status)}</Badge></div>
    <div className="text-sm md:col-span-3 md:text-right">{nok(o.calc.total)}{o.calc.outstanding > 0 && <div className="text-xs text-amber-400">Utestående {nok(o.calc.outstanding)}</div>}</div>
  </button>
);

export const WorkOrders = ({ openId, clearOpen }) => {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [category, setCategory] = useState("");
  const [payment, setPayment] = useState("");
  const [open, setOpen] = useState(null);
  const [items, reload] = useData("/admin/work-orders", { q, status, category, payment });
  useEffect(() => {
    if (openId) api.get(`/admin/work-orders/${openId}`).then((r) => setOpen({ ...r.data, parts: r.data.parts.map((p, i) => ({ id: `p${i}`, ...p })) })).catch(() => {}).finally(clearOpen);
  }, [openId, clearOpen]);
  return (
    <div data-testid="orders-page">
      <PageHead title="Arbeidsordre" sub={`${items?.length ?? 0} jobber`}>
        <ExportBtn kind="work_orders" testid="orders-export" />
        <Btn variant="red" onClick={() => setOpen({})} data-testid="order-new"><Plus size={14} /> Ny arbeidsordre</Btn>
      </PageHead>
      <div className="mb-4 grid gap-2 md:grid-cols-5">
        <div className="flex items-center gap-2 border border-white/15 px-3 md:col-span-2"><Search size={16} className="text-zinc-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Søk nr, kunde, telefon, reg.nr, feilkode" className="w-full bg-transparent py-2.5 text-sm outline-none" data-testid="orders-search" /></div>
        <Select value={status} onChange={(e) => setStatus(e.target.value)} options={[["", "Alle statuser"], ...JOB_STATUS]} data-testid="orders-filter-status" />
        <Select value={category} onChange={(e) => setCategory(e.target.value)} options={[["", "Alle kategorier"], ...CATEGORIES]} data-testid="orders-filter-category" />
        <Select value={payment} onChange={(e) => setPayment(e.target.value)} options={[["", "All betaling"], ...PAYMENT]} data-testid="orders-filter-payment" />
      </div>
      {!items ? <Spinner /> : (
        <div className="space-y-2" data-testid="orders-list">
          {items.length === 0 && <p className="border border-dashed border-white/15 p-10 text-center text-zinc-400">Ingen arbeidsordre.</p>}
          {items.map((o) => <OrderRow key={o.id} o={o} onClick={() => setOpen({ ...o, parts: o.parts.map((p, i) => ({ id: `p${i}`, ...p })) })} />)}
        </div>
      )}
      {open && <WorkOrderForm initial={open} onClose={() => { setOpen(null); reload(); }} onSaved={reload} />}
    </div>
  );
};
